import { forwardRef, HTMLProps, Ref } from 'react';
import cn from 'classnames';

import { GridCol } from '../../foundation/grid/GridCol';
import { Icon, IconNames } from '../../atoms/icon/Icon';
import { IconColors } from '../../assets/icons/index';
import { Text } from '../../../components/atoms/text/Text';
import { Title } from '../../../components/atoms/title/Title';
import './alert.scss';
import '../../assets/styles/utilities.scss';

export type Severity = 'default' | 'error' | 'information' | 'success' | 'warning';

export interface AlertProps extends Omit<HTMLProps<HTMLDivElement>, 'css'> {
  /**
   * Child component that is displayed instead of the content.
   */
  children?: JSX.Element | string;
  /**
   * The text to be shown in the alert's title.
   */
  title?: string;
  /**
   * Determines the strong color of the background.
   */
  bold?: boolean;
  /**
   * Indicates if the alert can be dismissed or not.
   * @default false
   */
  dismissible?: boolean;
  /**
   * Determines which icon should be shown.
   */
  icon?: IconNames;
  /**
   * Click handler when the dismiss button is pressed
   */
  onDismiss?: (e: React.MouseEvent) => void | unknown;
  /**
   * Determines the alert appearance.
   */
  severity?: Severity;
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLDivElement>;
}

const AlertComponent = (
  {
    bold,
    children,
    dismissible = false,
    icon,
    onDismiss,
    severity = 'default',
    title,
    classNames = [],
    ...props
  }: AlertProps,
  ref: AlertProps['ref'],
) => {
  let iconColor: IconColors;

  switch (severity) {
    case 'success':
      icon = icon ?? bold ? 'warning_circle_check' : 'feedback_success';
      iconColor = bold ? 'N00' : 'G200';
      break;
    case 'error':
      icon = icon ?? bold ? 'edit_close_circle' : 'feedback_critical';
      iconColor = bold ? 'N00' : 'R300';
      break;
    case 'warning':
      icon = icon ?? bold ? 'warning_triangle_warning' : 'feedback_warning';
      iconColor = bold ? 'N800' : 'Y300';
      break;
    case 'information':
      icon = icon ?? bold ? 'warning_info' : 'feedback_informational';
      iconColor = bold ? 'N00' : 'B200';
      break;
    default:
      icon = icon ?? 'warning_info';
      iconColor = bold ? 'N00' : 'N800';
      break;
  }

  const classAlert = 'lsux-alert';
  const cnAlert = cn(
    {
      [`${classAlert}`]: true,
      [`${classAlert}--${severity}--bold`]: bold,
      [`${classAlert}--${severity}--subtle`]: !bold,
      ['p-4']: true,
    },
    classNames,
  );

  const classAlertHeader = `${classAlert}__header`;
  const cnAlertHeader = cn(classAlertHeader, `${classAlertHeader}-wrapper`);

  return (
    <div className={cnAlert} ref={ref} {...props}>
      <div className={cnAlertHeader}>
        <GridCol>{icon && <Icon name={icon} classNames={[`${classAlert}__icon`]} color={iconColor} />}</GridCol>
        <GridCol>
          {title ? (
            <div>
              <Title text={title} textSize="medium" textWeight="bold" classNames={['pb-1']} />
            </div>
          ) : null}
          <div className={`${classAlert}--content-text`}>
            {typeof children === 'string' ? <Text textSize="small">{children}</Text> : children}
          </div>
        </GridCol>
        <GridCol>
          {dismissible && onDismiss !== undefined && (
            <Icon name="menu_close_md" color={iconColor} size="medium-small" onClick={onDismiss} />
          )}
        </GridCol>
      </div>
    </div>
  );
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(AlertComponent);
